import {
  Article,
  BlogPosting,
  BreadcrumbList,
  Collection,
  FAQPage,
  ListItem,
  Organization,
  Person,
  Question,
  Thing,
  WithContext,
} from 'schema-dts';

import {
  Author,
  FaqPagemainContentUnion,
  Page,
  PageSchemaType,
  SectionrowsUnion,
  Seo,
  Specialty,
  SpecialtyPagespecialtyComponentsUnion,
} from '@/lib/types';

import { FaqPage } from '@/features/faq-page/type';
import { JobCategoryPage } from '@/features/job-category-page/types';
import { SpecialtyPage } from '@/features/specialty-page';
import { getSchemaBuilder } from '@/hygraph/renderers';
import { routes } from '@/utils/route-utils';

export type TBreadcrumb = { title: string; path?: string };
type TComponent =
  | SectionrowsUnion
  | SpecialtyPagespecialtyComponentsUnion
  | FaqPagemainContentUnion;

type TPages = Page | SpecialtyPage | JobCategoryPage | FaqPage;

export type TSchemaContext = { specialty?: Specialty };
export type TSchemaData<T extends Thing> = {
  images: string[];
  questions: Question[];
  things: WithContext<T>[];
};

export const nomadHealthOrg: Organization = {
  '@type': 'Organization',
  name: 'Nomad Health',
  legalName: 'Nomad Health, Inc.',
  url: 'https://nomadhealth.com/',
};

const SchemaService = {
  buildSchemaData: buildSchemaData,
  buildPageThing: buildPageThing,
  buildBreadcrumbsList: buildBreadcrumbsList,
};

export default SchemaService;

function buildPageThing(
  page: TPages,
  images: string[]
): Article | BlogPosting | FAQPage | Collection {
  let seo: Seo;
  if ('templateDetails' in page) {
    seo = page.templateDetails.seo as Seo;
  } else {
    seo = page.seo as Seo;
  }

  const validImages = [seo?.image?.url, ...images].filter(
    (image): image is string => !!image
  );

  const { datePublished, dateModified } = buildPageDates(page);

  const schemaType =
    (page.__typename !== 'SpecialtyPage' &&
      seo?.schemaType !== 'Collection' &&
      seo?.schemaType) ||
    PageSchemaType.Article;

  const author =
    ('author' in page && page.author && [buildAuthorThing(page.author)]) || [];

  return {
    '@type': schemaType,
    headline: seo?.title || page.title || '',
    image: Array.from(new Set(validImages)),
    datePublished,
    dateModified,
    author,
  };
}

function buildSchemaData(
  components: TComponent[],
  context?: TSchemaContext
): TSchemaData<Thing> {
  return components.reduce(
    (output: TSchemaData<Thing>, component: TComponent) => {
      const buildSchema = getSchemaBuilder(component.__typename);
      if (!buildSchema) return output;
      const { images, things, questions } = buildSchema(component, context);
      return {
        images: output.images.concat(images || []),
        things: output.things.concat(things || []),
        questions: output.questions.concat(questions || []),
      };
    },
    { images: [], things: [], questions: [] } as TSchemaData<Thing>
  );
}

function buildBreadcrumbsList(
  breadcrumbs: TBreadcrumb[]
): WithContext<BreadcrumbList> {
  const host = routes.domain();
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => {
      const listItem: ListItem = {
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.title,
      };
      if (breadcrumb.path) {
        // TODO: Find a way to make sure the final URL is valid without crashing
        listItem.item = `${host}${breadcrumb.path}`;
      }
      return listItem;
    }),
  };
}

function buildAuthorThing(author: Author): Person {
  const [firstName, lastName] = author.name?.split(' ') || ([] as undefined[]);
  const person: Person = { '@type': 'Person', worksFor: nomadHealthOrg };
  if (author.name) person.name = author.name;
  if (firstName) person.givenName = firstName;
  if (lastName) person.familyName = lastName;
  if (author.professionalTitle) person.jobTitle = author.professionalTitle;
  if (author.linkedInProfile) person.sameAs = author.linkedInProfile;
  if (author.website) person.url = author.website;
  return person;
}

function buildPageDates(page: TPages) {
  if (page.__typename === 'SpecialtyPage' || page.__typename === 'FaqPage') {
    return {
      datePublished: page.publishedAt.toString(),
      dateModified: page.modifiedAt.toString(),
    };
  }
  if (page.__typename === 'JobCategoryPage') {
    const datePublished = page.publishedAt;
    return { datePublished, dateModified: datePublished };
  }
  const datePublished = page.published || page.publishedAt;
  return { datePublished, dateModified: datePublished };
}
